<template>
  <div></div>
</template>
<script setup lang="ts">
import message from 'ant-design-vue/es/message'
import { useWebSocket } from '@/hooks/useWebSocket'
import router from '@/router'
import { useUserInfo } from '@/store'
import mittBus from '@/utils/mitt'

const userStore = useUserInfo()

const emit = defineEmits(['rollback', 'update:visible', 'syncMessage'])

const props = withDefaults(
  defineProps<{
    /** 是否显示/开启 WebSocket（v-model:visible） */
    visible?: boolean
    /** WebSocket 服务地址，不传则使用 VITE_WS_URL */
    uri?: string
    /** 设备 ID，不传则不会建立连接（由 Hook 内部校验） */
    did?: number
  }>(),
  {
    visible: false,
    uri: undefined,
    did: undefined,
  },
)

const { t } = useI18n()

const websocketVisible = computed({
  get: () => props.visible,
  set: (val) => {
    emit('update:visible', val)
  },
})
// 使用 WebSocket Composable（传入 getter 以支持 props 变更时重连）
const {
  webSocket,
  reconnectTime,
  initWebSocket,
  close: closeWebSocket,
} = useWebSocket(
  {
    uri: () => props.uri,
    did: () => props.did,
    maxReconnect: 4,
    heartbeatInterval: 10 * 1000, // 10 秒
    inactivityTimeout: 5 * 60 * 1000, // 5 分钟
  },
  {
    onOpen: () => {
      console.log('WebSocket 连接成功')
    },
    onError: (event) => {
      console.log('WebSocket 连接错误', event)
      // 只有真正的连接错误才触发登出逻辑
      // missing_did 和 missing_uri 是配置错误，不应该登出
      if (event.type !== 'missing_did' && event.type !== 'missing_uri') {
        handleWebSocketError()
      }
    },
    onClose: (event) => {
      console.log('WebSocket 连接关闭', event)
      websocketVisible.value = false
    },
    onMessage: (data) => {
      // 处理无消息超时
      if (data.type === 'inactivity_timeout') {
        // 关闭连接并设置 visible，然后显示错误消息
        close()
        message.error(t('monitor.gathertip'))
        return
      }
      // 处理达到最大重连次数
      if (data.type === 'max_reconnect_reached') {
        message.error(t('monitor.collectFail'))
        close()
        return
      }
      // 转发消息给父组件
      emit('syncMessage', data)
    },
  },
)

// 处理 WebSocket 错误（业务逻辑）
const handleWebSocketError = () => {
  closeWebSocket()
  message.error(t('E_INVALID_OR_EXPIRED_TOKEN'))
  mittBus.emit('logout')
  localStorage.removeItem('TOKEN')
  localStorage.removeItem('HOST')
  userStore.$reset()
  router.replace('/login')
}

// 关闭连接（包含业务逻辑）
const close = () => {
  closeWebSocket()
  websocketVisible.value = false
}

onMounted(() => {
  initWebSocket()
})

onUnmounted(() => {
  console.log('组件销毁，关闭 WebSocket')
  closeWebSocket()
})

// 暴露给父组件（保持向后兼容）
defineExpose({
  state: {
    webSocket,
    reconnectTime,
  },
  close,
})
</script>
<style>
a {
  text-decoration: underline;
  color: var(--el-color-primary);
}
</style>
